import {
  User,
  Message,
  MessagesData,
  RoomUser,
  DeviceStatusEnum,
  RecognitionModelInfo,
} from '@/types';
import { MutationTree } from 'vuex';
import {
  ADD_NEW_MESSAGE,
  SET_CHAT_USER,
  SET_ME_RESOLVED,
  SET_ROOM_IS_ACTIVE,
  EDIT_MESSAGE,
  UPDATE_GUEST_IS_TYPING,
  UPDATE_USER_IS_PAUSED,
  SET_MESSAGES,
  RESET_DATA,
  CHANGE_FONT_SETTING,
  SET_SETTINGS_USER,
  SET_USERS,
  CHANGE_IS_PAUSED_RECOGNITION,
  CHANGE_IS_ACTIVE_RECOGNITION,
  CHANGE_IS_CAMERA_DOUBLE_WIDTH,
  CHANGE_USER_AUDIO_DEVICE_STATUS,
  CHANGE_USER_VIDEO_DEVICE_STATUS,
  SET_CONVERSATION_INFO,
  SET_IS_SIGNER_GUIDE_ACCEPTED,
  SET_RECOGNITION_MODELS,
} from './mutationTypes';
import { ChatState, FontSettingsType } from './types';
import { ConversationInfo } from '@/pages/Start/types';

export const mutations: MutationTree<ChatState> = {
  [SET_ROOM_IS_ACTIVE](state, active: boolean) {
    state['active'] = active;
  },

  [SET_CONVERSATION_INFO](state, info: ConversationInfo) {
    state.conversationInfo = info || { id: '', secret: {} };
  },

  [SET_ME_RESOLVED](state, value: boolean) {
    state.isMeResolved = value;
  },

  [SET_CHAT_USER](state, user: User) {
    if (state.me?.id === user.id) {
      state.me = {
        ...state.me,
        ...user,
      };
    } else {
      state.me = {
        ...user,
      };
    }
  },

  [SET_USERS](state, users: RoomUser[]) {
    state['users'] = users;
    const me = users.find((user) => user.id === state.me?.id);
    if (me !== undefined) {
      state.me['paused'] = me.paused;
      state.me['videoDeviceStatus'] = me.videoDeviceStatus;
      state.me['audioDeviceStatus'] = me.audioDeviceStatus;
    }
  },

  [ADD_NEW_MESSAGE](state, message: Message) {
    if (!state.messages.some((msg) => msg.id === message.id)) {
      state.messages[state.messages.length] = message;
      state.allMessagesCount = state.allMessagesCount++;
    }
  },

  [EDIT_MESSAGE](state, message: Message) {
    const index = state.messages.findIndex((msg) => msg.id === message.id);
    if (index > -1) {
      state.messages[index] = message;
    }
  },

  [UPDATE_GUEST_IS_TYPING](state, idGuest: string) {
    state['idGuestIsTyping'] = idGuest;
  },

  [UPDATE_USER_IS_PAUSED](state, payload: { userId: string; paused: boolean }) {
    if (state.me?.id === payload.userId) {
      state.me['paused'] = payload.paused;
      return;
    }

    if (state.me?.id !== payload.userId) {
      const index = state.users.findIndex((user: RoomUser) => user.id === payload.userId);
      if (index !== -1) {
        const usersArr = [...state.users];
        usersArr[index] = {
          ...state.users[index],
          paused: payload.paused,
        };
        state.users = usersArr;
      }
    }
  },

  [SET_MESSAGES](state, data: MessagesData) {
    state['allMessagesCount'] = data.allMessagesCount;
    state['messages'] = data.messages;
  },

  [RESET_DATA](state) {
    state.active = false;
    state.idGuestIsTyping = '';
    state.messages = [];
    state.users = [];
    if (state.me) {
      state.me['paused'] = true;
    }
  },

  [CHANGE_FONT_SETTING](state, payload: { key: string; value: string }) {
    state.fontSettings[payload.key as keyof FontSettingsType] = payload.value;
  },

  [SET_SETTINGS_USER](state, settings: FontSettingsType) {
    state['fontSettings'] = settings;
  },

  [CHANGE_IS_PAUSED_RECOGNITION](state, value: boolean) {
    state.isPausedRecognizing = value;
  },

  [CHANGE_IS_CAMERA_DOUBLE_WIDTH](state, isDoubleWidth: boolean) {
    state['isCameraDoubleWidth'] = isDoubleWidth;
  },

  [SET_IS_SIGNER_GUIDE_ACCEPTED](state, isAccepted: boolean) {
    state.isSignerGuideAccepted = isAccepted;
  },

  [CHANGE_IS_ACTIVE_RECOGNITION](state, payload: { isActive: boolean; warning?: string }) {
    if (payload.isActive) {
      state.recognitionStatus['launched'] = payload.isActive;
      return;
    }

    if (!payload.isActive && payload.warning !== undefined) {
      state.recognitionStatus['launched'] = payload.isActive;
      state.recognitionStatus['warning'] = payload.warning;
    }
  },

  [CHANGE_USER_AUDIO_DEVICE_STATUS](state, payload: { userId: string; status: DeviceStatusEnum }) {
    if (state.me?.id === payload.userId) {
      state.me['audioDeviceStatus'] = payload.status;
      return;
    }

    if (state.me?.id !== payload.userId) {
      const index = state.users.findIndex((user: RoomUser) => user.id === payload.userId);
      if (index !== -1) {
        const usersArr = [...state.users];
        usersArr[index] = {
          ...state.users[index],
          audioDeviceStatus: payload.status,
        };
        state.users = usersArr;
      }
    }
  },

  [CHANGE_USER_VIDEO_DEVICE_STATUS](state, payload: { userId: string; status: DeviceStatusEnum }) {
    if (state.me?.id === payload.userId) {
      state.me['videoDeviceStatus'] = payload.status;
      return;
    }

    if (state.me?.id !== payload.userId) {
      const index = state.users.findIndex((user: RoomUser) => user.id === payload.userId);
      if (index !== -1) {
        const usersArr = [...state.users];
        usersArr[index] = {
          ...state.users[index],
          videoDeviceStatus: payload.status,
        };
        state.users = usersArr;
      }
    }
  },

  [SET_RECOGNITION_MODELS](state, models: RecognitionModelInfo[]) {
    state.availableModels = models;
  },

  userSignedOut: (state) => {
    state.active = false;
    state.messages = [];
    state.users = [];
    state.me = {
      id: '',
      firstName: '',
      lastName: '',
      userMode: '',
      paused: false,
      isAcknowledged: false,
      isAuthorized: false,
      email: '',
      token: '',
      audioDeviceStatus: DeviceStatusEnum.active,
      videoDeviceStatus: DeviceStatusEnum.active,
    };
    state.conversationInfo = <ConversationInfo>{
      code: '',
      id: '',
    };
  },
};
